// MUST stay the first import. It reads the payment-link token out of the URL
// fragment (/pay#t=…) and removes the fragment before any other module of
// the app — telemetry, router, providers — has been evaluated.
import { isPayPath } from './pay/payLinkToken.js';
import { StrictMode } from 'react';
import { installTelemetry } from './telemetry/journey.js';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from './auth/tokenStorage.js';
import { ThemeProvider } from './theme/theme.jsx';
import { AuthProvider } from './context/AuthContext.jsx';
import { SessionProvider } from './context/SessionContext.jsx';
import { NotificationProvider } from './context/NotificationContext.jsx';
import { ToastProvider } from './context/ToastContext.jsx';

// Guest-session recovery cookie MUST ride cross-origin XHR from
// ev.greencharge.app → api.greencharge.app both ways: the browser needs
// to store the `Set-Cookie: gc_gsr_<uid>=…; HttpOnly; Secure; SameSite=Lax`
// returned by POST /api/ev/sessions AND replay it on every subsequent
// /api/ev/sessions/:uid/* request. Axios defaults `withCredentials: false`,
// which silently drops the cookie in both directions and surfaces as
// SESSION_AUTH_REQUIRED on the very next call. Backend CORS already
// sets `Access-Control-Allow-Credentials: true` with an exact-origin
// allow list — this line turns on the client half. Must be set BEFORE
// any axios call in the app; main.jsx is the earliest entry point.
//
// The payment page (/pay) is the exception to all of this. Its only
// credential is the link token, sent by its own axios instance
// (api/payLinkApi.js); the document gets no cookies-by-default, no Bearer
// interceptor and no journey telemetry, because the telemetry reports the
// page path with every event and the request path with every failed
// default-axios call. Decided once, from the path this document loaded on.
const PAY_PAGE = isPayPath();

if (!PAY_PAGE) axios.defaults.withCredentials = true;

// Attach the signed-in driver's Bearer to every default-axios request.
// sessionsApi.js uses the default axios instance for the whole session
// lifecycle (create → preauth → start → poll → stop). Without this, those
// requests carry no Authorization header, so:
//   • POST /api/ev/sessions creates the session with user_id = NULL — a
//     signed-in driver is silently downgraded to an anonymous guest, and
//   • the driver can never mint a session stream ticket (the customer
//     branch 404s in findSessionForUser on ownership), so the ticket/SSE
//     path is unreachable and every signed-in driver is forced onto the
//     cookie/polling recovery path.
// A guest has no access token, so getAccessToken() returns null and this
// is a no-op for them — they continue to authorise via the HttpOnly
// recovery cookie (withCredentials, above). An expired/invalid token is
// still sent; the backend rejects it (401 SESSION_AUTH_REQUIRED on guarded
// routes) rather than silently creating an anonymous session, which the
// auth-failed interceptor turns into the login/recovery UI.
// Product telemetry: journey/trace headers to the API, batched events.
if (!PAY_PAGE) {
  installTelemetry(axios);

  axios.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

// Foundation styles — imported before the shell so tokens apply everywhere.
import './styles/global.less';
import './index.less';

import AppRoutes from './AppRoutes.jsx';
import PayRoot from './pay/PayRoot.jsx';
import MaintenancePage from './pages/Maintenance/MaintenancePage.jsx';
import { isMaintenanceMode } from './config/maintenance';
import { bootstrapNative } from './platform/bootstrap.js';
import { isNative } from './platform/platform.js';

// Flip on the theme-transition marker after first paint so the very
// first render doesn't animate from an unstyled state.
if (typeof requestAnimationFrame !== 'undefined' && typeof document !== 'undefined') {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-ready');
    });
  });
}

function renderApp() {
  createRoot(document.getElementById('root')).render(
    <StrictMode>
      {/*
        Maintenance gate. When VITE_MAINTENANCE_MODE is exactly the
        string "true", replace the entire user app with the maintenance
        screen — no Router, no auth/session providers, no charger
        lookup, no API calls. QR-code and other nested routes never
        mount because the router itself does not mount.
      */}
      {isMaintenanceMode() ? (
        <MaintenancePage />
      ) : PAY_PAGE ? (
        // The payment page: theme + router + page, and nothing else. No
        // Auth / Session / Notification providers — see pay/PayRoot.jsx.
        <PayRoot />
      ) : (
        <ThemeProvider>
          <Router>
            <AuthProvider>
              <SessionProvider>
                <NotificationProvider>
                  <ToastProvider>
                    <AppRoutes />
                  </ToastProvider>
                </NotificationProvider>
              </SessionProvider>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      )}
    </StrictMode>,
  );
}

// Native path: await bootstrap BEFORE the first render so the
// AuthProvider sees the token Capacitor Preferences durably stored
// on the previous launch. Web path: render immediately (bootstrapNative
// is a no-op on web besides toggling the `.gc-native` class) — this
// keeps the web startup and its test coverage synchronous, matching
// the historical behaviour that the axiosCredentials regression test
// asserts.
if (isNative()) {
  bootstrapNative()
    .catch(() => { /* boot must always continue */ })
    .then(renderApp);
} else {
  // Fire-and-forget so the class toggle still lands; the toggle is
  // idempotent and doesn't need to complete before React mounts.
  bootstrapNative().catch(() => {});
  renderApp();
}
